import React, { useState, useEffect } from "react";
import { Spinner, Alert } from "react-bootstrap";

const TestimonialSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [testimonialData, setTestimonialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTestimonialData = async () => {
      try {
        const response = await fetch("https://ecom-22-fe6311cae608.herokuapp.com/api/testimonial-section");
        const data = await response.json();
        
        if (data.success) {
          setTestimonialData(data.data);
        } else {
          setError(data.message || "Failed to fetch testimonial section data");
        }
      } catch (err) {
        setError("Network error. Please try again later.");
        console.error("Error fetching testimonial section:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonialData();
  }, []);

  const nextTestimonial = () => {
    if (testimonialData && testimonialData.testimonials) {
      setActiveIndex((prevIndex) => (prevIndex + 1) % testimonialData.testimonials.length);
    }
  };

  const prevTestimonial = () => {
    if (testimonialData && testimonialData.testimonials) {
      setActiveIndex(
        (prevIndex) => (prevIndex - 1 + testimonialData.testimonials.length) % testimonialData.testimonials.length
      );
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <i
        key={i}
        className={`fas fa-star ${i < rating ? "text-warning" : "text-muted"}`}
      ></i>
    ));
  };

  if (loading) {
    return (
      <section className="testimonial-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden"></span>
                </Spinner>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="testimonial-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <Alert variant="danger">{error}</Alert>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!testimonialData || !testimonialData.isActive) {
    return null; // Don't render if data is not available or section is inactive
  }

  return (
    <>
      <style jsx>{`
        .testimonial-section {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 80px 0;
          position: relative;
        }

        .testimonial-card {
          background: white;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          padding: 40px;
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .testimonial-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12);
        }

        .quote-icon {
          position: absolute;
          top: 20px;
          left: 20px;
          font-size: 60px;
          color: rgba(0, 123, 255, 0.1);
          z-index: 0;
        }

        .testimonial-content {
          position: relative;
          z-index: 1;
        }

        .testimonial-text {
          font-size: 1.1rem;
          line-height: 1.8;
          color: #495057;
          margin-bottom: 25px;
          font-style: italic;
        }

        .author-avatar {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background-color: #e9ecef;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          color: #495057;
          font-size: 1.5rem;
        }

        .testimonial-nav {
          position: absolute;
          bottom: -20px;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          gap: 10px;
        }

        .nav-dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background-color: #dee2e6;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .nav-dot.active {
          background-color: #007bff;
          transform: scale(1.2);
        }

        .testimonial-image {
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
          position: relative;
        }

        .testimonial-image img {
          width: 100%;
          height: auto;
          transition: transform 0.5s ease;
        }

        .testimonial-image:hover img {
          transform: scale(1.03);
        }

        .satisfaction-badge {
          position: absolute;
          bottom: -15px;
          right: -15px;
          background: white;
          border-radius: 10px;
          padding: 15px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          z-index: 2;
        }

        .carousel-control {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: white;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
          z-index: 3;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .carousel-control:hover {
          background: #f8f9fa;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
        }

        .carousel-control.prev {
          left: -20px;
        }

        .carousel-control.next {
          right: -20px;
        }

        @media (max-width: 992px) {
          .testimonial-section {
            padding: 60px 0;
          }

          .testimonial-card {
            padding: 30px;
          }

          .carousel-control.prev {
            left: 10px;
          }

          .carousel-control.next {
            right: 10px;
          }
        }
      `}</style>

      <section className="testimonial-section">
        <div className="container">
          <div className="row justify-content-center mb-5">
            <div className="col-lg-8 text-center">
              <h2 className="display-5 fw-bold mb-3">
                {testimonialData.title}
              </h2>
              <p className="lead text-muted">
                {testimonialData.subtitle}
              </p>
            </div>
          </div>

          <div className="row align-items-center">
            {/* Image Section */}
            <div className="col-lg-5 mb-4 mb-lg-0">
              <div className="testimonial-image">
                <img
                  src={testimonialData.imageUrl}
                  alt="Business professionals"
                  className="img-fluid"
                />
                <div className="satisfaction-badge">
                  <div className="text-center">
                    <div className="h4 mb-0">{testimonialData.satisfactionPercentage}%</div>
                    <div className="small text-muted">Client Satisfaction</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial Section */}
            <div className="col-lg-7">
              <div className="position-relative">
                <div className="testimonial-card">
                  <i className="fas fa-quote-left quote-icon"></i>
                  <div className="testimonial-content">
                    <div className="mb-4">
                      {renderStars(testimonialData.testimonials[activeIndex].rating)}
                    </div>
                    <p className="testimonial-text">
                      {testimonialData.testimonials[activeIndex].text}
                    </p>
                    <div className="d-flex align-items-center">
                      <div className="author-avatar me-3">
                        {testimonialData.testimonials[activeIndex].author.charAt(0)}
                      </div>
                      <div>
                        <h5 className="mb-0 fw-bold">
                          {testimonialData.testimonials[activeIndex].author}
                        </h5>
                        <p className="text-muted mb-0">
                          {testimonialData.testimonials[activeIndex].position}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="testimonial-nav">
                    {testimonialData.testimonials.map((_, index) => (
                      <div
                        key={index}
                        className={`nav-dot ${
                          index === activeIndex ? "active" : ""
                        }`}
                        onClick={() => setActiveIndex(index)}
                      ></div>
                    ))}
                  </div>
                </div>

                <div
                  className="carousel-control prev"
                  onClick={prevTestimonial}
                >
                  <i className="fas fa-chevron-left"></i>
                </div>
                <div
                  className="carousel-control next"
                  onClick={nextTestimonial}
                >
                  <i className="fas fa-chevron-right"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default TestimonialSection;